import React from 'react';

// Debug logging helper
const logDebug = (action, details) => {
    const timestamp = new Date().toISOString();
    console.debug(`[CSRF ${timestamp}] ${action}:`, details);
};

// Performance logging helper
const logTiming = async (operation, fn) => {
    const start = performance.now();
    try {
        const result = await fn();
        const duration = performance.now() - start;
        logDebug(`${operation} completed`, { durationMs: duration.toFixed(2) });
        return result;
    } catch (error) {
        const duration = performance.now() - start;
        logDebug(`${operation} failed`, { durationMs: duration.toFixed(2), error });
        throw error;
    }
};

// Cache the CSRF token
let csrfToken = null;
let tokenExpiryTime = null;
let tokenFetchPromise = null;

// Function to get a valid CSRF token
export const getCsrfToken = async () => {
    const now = Date.now();
    
    // If we have a valid token that's not close to expiry (5 minutes buffer), return it
    if (csrfToken && tokenExpiryTime && (tokenExpiryTime - now > 5 * 60 * 1000)) {
        logDebug('Using cached token', {
            expiresIn: Math.round((tokenExpiryTime - now) / 1000) + 's'
        });
        return csrfToken;
    }
    
    // If there's already a token fetch in progress, wait for it
    if (tokenFetchPromise) {
        logDebug('Waiting for in-progress token fetch');
        return tokenFetchPromise;
    }
    
    try {
        tokenFetchPromise = logTiming('fetchCsrfToken', async () => {
            // Request new token
            const response = await fetch('/api/v1/csrf/token', {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            
            if (!response.ok) {
                throw new Error(`Failed to fetch CSRF token: ${response.status}`);
            }
            
            const data = await response.json();
            csrfToken = data.csrfToken;
            // Set expiry time to 25 minutes (5 minutes before actual expiry)
            tokenExpiryTime = now + (25 * 60 * 1000);
            
            logDebug('New token fetched', {
                expiresIn: '25m',
                tokenPresent: !!csrfToken
            });
            
            return csrfToken;
        });
        
        return await tokenFetchPromise;
    } catch (error) {
        logDebug('Token fetch error', {
            error: error?.message,
            stack: error?.stack
        });
        throw error;
    } finally {
        tokenFetchPromise = null;
    }
};

// Function to add CSRF token to headers
export const addCsrfHeader = async (headers = {}) => {
    const token = await getCsrfToken();
    return {
        ...headers,
        'x-csrf-token': token
    };
};

// Enhanced fetch function with CSRF token
export const fetchWithCSRF = async (url, options = {}) => {
    const requestId = Math.random().toString(36).substring(7);
    logDebug('Request started', {
        requestId,
        url,
        method: options.method || 'GET'
    });
    
    const start = performance.now();
    try {
        const csrfHeaders = await addCsrfHeader(options.headers);
        const fetchOptions = {
            ...options,
            headers: csrfHeaders,
            credentials: 'include', // Always include credentials for CSRF
        };
        
        const response = await fetch(url, fetchOptions);
        const duration = performance.now() - start;
        
        logDebug('Request completed', {
            requestId,
            url,
            status: response.status,
            ok: response.ok,
            durationMs: duration.toFixed(2)
        });
        
        // Log non-200 responses with more detail (but suppress common GET failures)
        if (!response.ok) {
            // Suppress logging for common GET failures (404, 403) on image endpoints
            const isImageEndpoint = url.includes('/recipe-images/') || url.includes('/ingredient-images/');
            const isCommonGetFailure = (response.status === 403 || response.status === 404) && 
                                       (options.method || 'GET') === 'GET';
            
            if (isImageEndpoint && isCommonGetFailure) {
                // Silently fail for missing images
                return response;
            }
            
            try {
                // Clone the response so we don't consume the original body's readable stream
                const clonedResponse = response.clone();
                const errorText = await clonedResponse.text();
                logDebug('Response error details', {
                    requestId,
                    url,
                    status: response.status,
                    statusText: response.statusText,
                    errorBody: errorText.substring(0, 200) // Truncate long errors
                });
            } catch (e) {
                logDebug('Failed to read error response', {
                    requestId,
                    error: e?.message
                });
            }
        }
        
        return response;
    } catch (error) {
        const duration = performance.now() - start;
        logDebug('Request failed', {
            requestId,
            url,
            error: error?.message,
            stack: error?.stack,
            durationMs: duration.toFixed(2)
        });
        throw error;
    }
};

// Hook for React components to manage CSRF token
export const useCSRF = () => {
    const [isLoading, setIsLoading] = React.useState(false);
    const [error, setError] = React.useState(null);

    const fetchToken = React.useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            await getCsrfToken();
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, []);

    React.useEffect(() => {
        fetchToken();
    }, [fetchToken]);

    return {
        isLoading,
        error,
        refetch: fetchToken,
        getToken: getCsrfToken,
        addCsrfHeader,
        fetchWithCSRF
    };
};

// Utility to check if a request needs CSRF protection
export const needsCSRFProtection = (method, url) => {
    // GET requests don't need CSRF protection
    if (method.toUpperCase() === 'GET') {
        return false;
    }
    // Exclude webhook routes and CSRF token endpoint
    const excludedPaths = [
        '/api/v1/supabase/webhook',
        '/api/v1/csrf/token'
    ];
    return !excludedPaths.some(path => url.includes(path));
};

// Clear stored token (useful for logout)
export const clearCSRFToken = () => {
    csrfToken = null;
    tokenExpiryTime = null;
}; 